import { autoinject, transient } from "aurelia-dependency-injection";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import {
    GetOnboardingClientConfigResponse, CreateOnboardingRequest, CreateOnboardingRequestResponse,
    BaseOnboardingRequest, OfferingsForOnboardingRequestResponse, OfferingsSelectionRequest, OfferingsSelectionResponse,
    GetAdvancedMetaResponse, UpdateAdvancedMetaRequest, UploadDocumentsRequest,
    CompleteOnboardingResponse,
    GetRequiredDocumentTypesResponse, GetVerificationsResponse, SaveVerificationMetaResponse, UpdateAdvancedMetaResponse,
    UploadFilesResponse, GetOnboardingRequestResponse, ValidateOnboardingRequest, OnboardingModelValidationResponse,
    ValidateOnboardingOfferingsRequest, ValidateOnboardingOfferingsResponse, ValidateOnboardingOfferingRequest,
    ValidatedOnboardingOfferingResponse, GetRequiredDocumentTypesRequest, UploadDocumentsResponse
} from "../../models/onboarding/onboarding.models";
import { OnboardingClientUrls } from '../../constants/onboarding/onboarding.constants';

@autoinject()
@transient()
export class OnboardingClientService {

    private _requiredDocumentTypesPromise: { [key: string]: Promise<GetRequiredDocumentTypesResponse> } = {};

    constructor(private requestClient: RequestClientService) {
    }

    shortenUrl(url: string): Promise<string> {
        return this.requestClient.get(OnboardingClientUrls.ShortenUrl, { url: url });
    }

    saveUrlsHistory(onboardingRequestGuid: string, urlsHistory: string[]): Promise<void> {
        return this.requestClient.post(OnboardingClientUrls.SaveUrlsHistory, {}, { onboardingRequestGuid, urlsHistory });
    }

    getOnboardingClientConfiguration(guid: string): Promise<GetOnboardingClientConfigResponse> {
        return this.requestClient.post(OnboardingClientUrls.GetOnboardingClientConfiguration, { guid: guid }, {});
    }

    validateOnboardingRequest(request: ValidateOnboardingRequest): Promise<OnboardingModelValidationResponse> {
        return this.requestClient.post(OnboardingClientUrls.ValidateOnboardingRequest, {}, request);
    }

    validateOnboardingOffering(request: ValidateOnboardingOfferingRequest): Promise<ValidatedOnboardingOfferingResponse> {
        return this.requestClient.post(OnboardingClientUrls.ValidateOnboardingOffering, {}, request);
    }

    validateOnboardingOfferings(request: ValidateOnboardingOfferingsRequest): Promise<ValidateOnboardingOfferingsResponse> {
        return this.requestClient.post(OnboardingClientUrls.ValidateOnboardingOfferings, {}, request);
    }

    createOrUpdateOnboardingRequest(request: CreateOnboardingRequest): Promise<CreateOnboardingRequestResponse> {
        return this.requestClient.post(OnboardingClientUrls.CreateOrUpdateOnboardingRequest, {}, request);
    }

    updateOfferingsSelection(request: OfferingsSelectionRequest): Promise<OfferingsSelectionResponse> {
        return this.requestClient.post(OnboardingClientUrls.UpdateOfferingsSelection, {}, request);
    }

    getOfferingsForOnboardingRequest(request: BaseOnboardingRequest): Promise<OfferingsForOnboardingRequestResponse> {
        return this.requestClient.post(OnboardingClientUrls.GetOfferingsForOnboardingRequest, {}, request);
    }

    getVerifications(guid: string): Promise<GetVerificationsResponse> {
        return this.requestClient.post(OnboardingClientUrls.GetVerifications, { guid: guid }, {});
    }

    getAdvancedMetaConfiguration(onboardingRequestGuid: string): Promise<GetAdvancedMetaResponse> {
        return this.requestClient.post(OnboardingClientUrls.GetAdvancedMetaConfiguration, {}, { onboardingRequestGuid: onboardingRequestGuid });
    }

    getRequiredDocumentTypes(request: GetRequiredDocumentTypesRequest): Promise<GetRequiredDocumentTypesResponse> {

        const key = JSON.stringify(request);

        if (!this._requiredDocumentTypesPromise[key]) {
            this._requiredDocumentTypesPromise[key] = this.requestClient.post(OnboardingClientUrls.GetRequiredDocumentTypes, {}, request);
        }
        return this._requiredDocumentTypesPromise[key];
    }

    updateAdvancedMeta(request: UpdateAdvancedMetaRequest): Promise<UpdateAdvancedMetaResponse> {
        return this.requestClient.post(OnboardingClientUrls.UpdateAdvancedMeta, {}, request);
    }

    validateUploadDocuments(request: UploadDocumentsRequest): Promise<void> {
        return this.requestClient.postFormData(OnboardingClientUrls.ValidateUploadDocuments, request);
    }

    uploadDocuments(request: UploadDocumentsRequest): Promise<UploadDocumentsResponse> {
        return this.requestClient.postFormData(OnboardingClientUrls.UploadDocuments, request);
    }

    rejectOnboardingRequest(request: BaseOnboardingRequest): Promise<CompleteOnboardingResponse> {
        return this.requestClient.post(OnboardingClientUrls.RejectOnboardingRequest, {}, request);
    }

    completeOnboardingRequest(request: BaseOnboardingRequest): Promise<CompleteOnboardingResponse> {
        return this.requestClient.post(OnboardingClientUrls.CompleteOnboardingRequest, {}, request);
    }

    saveVerificationMeta(request: { metaJson: string, verificationInternalName: string, retriesDone: number, onboardingRequestGuid: string }): Promise<SaveVerificationMetaResponse> {
        return this.requestClient.post(OnboardingClientUrls.SaveVerificationMeta, {}, request);
    }

    uploadBlobs(blobs: { blob: Blob, name: string }[]): Promise<UploadFilesResponse> {
        const formData = new FormData();
        blobs.forEach(file => {
            formData.append(file.name, file.blob, file.name);
        });
        return this.requestClient.postWithProgressCallback(OnboardingClientUrls.UploadFiles, () => { }, {}, formData);
    }

    uploadFiles(files: File[]): Promise<UploadFilesResponse> {
        const formData = new FormData();
        files.forEach(file => {
            formData.append(file.name, file);
        });
        return this.requestClient.postWithProgressCallback(OnboardingClientUrls.UploadFiles, () => { }, {}, formData);
    }

    getOnboardingRequest(): Promise<GetOnboardingRequestResponse> {
        return this.requestClient.post(OnboardingClientUrls.GetOnboardingRequest, {}, { onboardingRequestGuid: window["onboardingRequestUrlMeta"]["onboardingRequestGuid"] });
    }

    saveClientNotes(onboardingRequestGuid: string, clientNotes: string): Promise<void> {
        return this.requestClient.post(OnboardingClientUrls.SaveClientNotes, {}, { onboardingRequestGuid, clientNotes });
    }

    saveGdprConsent(onboardingRequestGuid: string, hasGdprConsent: boolean): Promise<void> {
        return this.requestClient.post(OnboardingClientUrls.SaveGdprConsent, {}, { onboardingRequestGuid, hasGdprConsent });
    }

    getSharedAccessSignatureToken(id: number, downloadFile: boolean): Promise<string> {
        return this.requestClient.get(OnboardingClientUrls.GetSharedAccessSignatureToken, { onboardingRequestDocumentId: id, downloadFile });
    }
}
